import { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { formatDate, getMediaTypeIcon } from "@/lib/utils";
import { MoreVertical, Download, Trash, Edit, Share, ExternalLink, Check, Scissors, Wand2, Star } from "lucide-react";
import VideoEditor from "./VideoEditor";

interface MediaItemProps {
  item: {
    id: string;
    title: string;
    description?: string;
    mediaType: string;
    mediaUrl: string;
    thumbnailUrl?: string;
    date?: string;
    createdAt: string;
    athleteId: string;
    isFavorite?: boolean;
  };
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  thumbnailHeight?: string;
}

// Resolve an S3 key to a displayable URL.
// HEIC/HEIF keys are routed through the server's /api/media/:id/image endpoint,
// which converts to JPEG server-side using sharp — reliable on all browsers.
// Everything else gets a standard presigned S3 GET URL.
function usePresignedUrl(raw: string, itemId?: string): string {
  const isHeic = /\.(heic|heif)$/i.test(raw);
  const [resolved, setResolved] = useState<string>("");

  useEffect(() => {
    if (!raw) return;
    let blobUrl = "";

    if (isHeic && itemId) {
      // HEIC images must be fetched via apiRequest so the Authorization header
      // is included. A plain <img src="/api/media/:id/image"> sends no headers
      // and hits a 401. We fetch as a blob and hand back a blob: URL instead.
      apiRequest(`/api/media/${itemId}/image`)
        .then((res) => (res.ok ? res.blob() : Promise.reject(`HTTP ${res.status}`)))
        .then((blob) => {
          blobUrl = URL.createObjectURL(blob);
          setResolved(blobUrl);
        })
        .catch((err) => console.warn("[HEIC fetch]", err));
      // Cleanup revokes the blob URL once the component unmounts or raw changes.
      return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    }

    if (raw.startsWith("http")) {
      setResolved(raw);
      return;
    }

    apiRequest(`/api/uploads/url?key=${encodeURIComponent(raw)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then(({ url }) => setResolved(url))
      .catch(() => setResolved(""));
  }, [raw, itemId, isHeic]);

  return resolved;
}

// Captures the first visible frame of a video as a static JPEG data URL.
// Using a canvas-captured image avoids the GPU compositing layer that raw <video>
// elements create, which would bleed through modal overlays regardless of z-index.
// Falls back to an empty string on CORS or decode errors (shows dark placeholder).
function useVideoThumbnail(videoUrl: string): string {
  const [thumbnail, setThumbnail] = useState<string>("");

  useEffect(() => {
    if (!videoUrl) return;
    let cancelled = false;

    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";

    const capture = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          setThumbnail(canvas.toDataURL("image/jpeg", 0.75));
        }
      } catch {
        // Security error if S3 CORS headers are missing — fall back silently
      }
      video.src = "";
    };

    video.addEventListener("seeked", capture, { once: true });
    video.addEventListener("loadedmetadata", () => {
      // Seek a little past 0 to get a real frame (not a blank first frame)
      video.currentTime = 0.1;
    }, { once: true });
    video.addEventListener("error", () => { video.src = ""; }, { once: true });

    video.src = videoUrl;
    video.load();

    return () => {
      cancelled = true;
      video.src = "";
    };
  }, [videoUrl]);

  return thumbnail;
}

const MediaItem = ({ item, selectable = false, selected = false, onSelect, thumbnailHeight = "h-32" }: MediaItemProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // HEIC keys are transparently routed through /api/media/:id/image (server converts to JPEG).
  // All other keys get a standard presigned S3 URL. displayUrl is always browser-renderable.
  const displayUrl = usePresignedUrl(item.mediaUrl, item.id);
  const mediaUrl = displayUrl; // alias used throughout for video/audio/pdf
  const videoThumbnail = useVideoThumbnail(item.mediaType === "video" ? displayUrl : "");
  const [isHovered, setIsHovered] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  
  // Touch gesture states for mobile interactions
  const [touchStart, setTouchStart] = useState<number>(0);
  const [touchDuration, setTouchDuration] = useState<number>(0);
  const [isLongPress, setIsLongPress] = useState<boolean>(false);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const touchTimeout = 500; // milliseconds before considered a long press

  const getMediaTypeStyle = (mediaType: string) => {
    switch (mediaType) {
      case "image":
        return "bg-primary-500";
      case "video":
        return "bg-secondary-500";
      case "audio":
        return "bg-accent-500";
      case "pdf":
        return "bg-neutral-700";
      default:
        return "bg-neutral-500";
    }
  };

  const handleDelete = async () => {
    if (confirm("Are you sure you want to delete this media file?")) {
      setIsDeleting(true);
      try {
        await apiRequest(`/api/media/${item.id}`, {
          method: 'DELETE'
        });
        setShowPreview(false);
        toast({
          title: "Media deleted",
          description: "Media file has been deleted successfully."
        });
        queryClient.invalidateQueries({ queryKey: ['/api/athletes', item.athleteId, 'media'] });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to delete media file.",
          variant: "destructive"
        });
      }
      setIsDeleting(false);
    }
  };

  const handleDownload = async () => {
    if (!mediaUrl) return;
    try {
      // Fetch as blob so the browser saves with the correct filename.
      // A direct <a href={presignedUrl} download> doesn't work cross-origin.
      const response = await fetch(mediaUrl);
      if (!response.ok) throw new Error(`fetch ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const ext = item.mediaUrl.split(".").pop()?.split("?")[0] || "";
      const filename = ext ? `${item.title}.${ext}` : item.title;
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast({ title: "Download failed", description: "Could not download this file. Please try again.", variant: "destructive" });
    }
  };

  const toggleFavorite = async () => {
    setIsTogglingFavorite(true);
    try {
      await apiRequest(`/api/media/${item.id}/favorite`, { method: 'PATCH' });
      toast({
        title: item.isFavorite ? "Removed from favorites" : "Added to favorites",
        description: item.isFavorite ? 
          "This media is no longer a favorite moment" : 
          "This media is now marked as a favorite moment"
      });
      queryClient.invalidateQueries({ queryKey: ["/api/athletes", item.athleteId, "media"] });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update favorite status",
        variant: "destructive"
      });
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const renderMediaPreview = () => {
    if (item.mediaType === "image") {
      return (
        <img
          src={mediaUrl}
          alt={item.title}
          className="w-full h-full max-h-full object-contain mx-auto block"
        />
      );
    } else if (item.mediaType === "video") {
      return (
        <div className="w-full h-full bg-black rounded-lg overflow-hidden flex items-center justify-center">
          <video
            src={mediaUrl}
            controls
            autoPlay
            className="w-full h-full max-h-full object-contain"
            playsInline
          >
            Your browser does not support video playback.
          </video>
        </div>
      );
    } else if (item.mediaType === "audio") {
      return (
        <div className="w-full flex flex-col items-center justify-center py-8">
          <div className="rounded-full bg-neutral-100 p-4 mb-4">
            <span className="material-icons text-4xl text-primary">mic</span>
          </div>
          <audio controls src={mediaUrl} className="w-full max-w-md">
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    } else if (item.mediaType === "pdf") {
      return (
        <div className="w-full h-[70vh] flex flex-col items-center">
          <object
            data={mediaUrl}
            type="application/pdf"
            width="100%"
            height="100%"
            className="border rounded-lg"
          >
            <p>
              Your browser doesn't support PDF preview.
              <a 
                href={mediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline ml-1"
              >
                Download instead
              </a>
            </p>
          </object>
        </div>
      );
    } else {
      return (
        <div className="text-center py-8">
          <div className="rounded-full bg-neutral-100 p-6 mx-auto mb-4 inline-block">
            <span className="material-icons text-4xl text-neutral-500">insert_drive_file</span>
          </div>
          <p>Preview not available for this file type</p>
          <a
            href={mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline inline-block mt-2"
          >
            Open file in new tab
          </a>
        </div>
      );
    }
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    // Set hover state for showing controls
    setIsHovered(true);
    
    // Record touch start time for long press detection
    const startTime = Date.now();
    setTouchStart(startTime);
    
    // Start long press timer
    longPressTimer.current = setTimeout(() => {
      setIsLongPress(true);
      setShowOptions(true);
      
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, touchTimeout);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    // Cancel long press if user moves finger
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Clear the long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    
    const endTime = Date.now();
    const pressDuration = endTime - touchStart;
    setTouchDuration(pressDuration);
    
    // If it was a long press, prevent default tap action
    if (isLongPress) {
      e.stopPropagation();
      setIsLongPress(false);
      
      // Keep hover state active for options menu
      return;
    }
    
    // If it was a quick tap
    if (pressDuration < touchTimeout) {
      // Handle tap normally (preview or select)
      if (!showOptions && !selectable) {
        setShowPreview(true);
      }
    }
    
    // Keep hover state active for a moment to allow interactions on mobile
    setTimeout(() => {
      if (!showOptions) {
        setIsHovered(false);
      }
    }, 2000);
  };

  const mediaTypeBadge = () => {
    if (item.mediaType === "video") return (
      <span className="absolute top-2 left-2 z-10 flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        VIDEO
      </span>
    );
    if (item.mediaType === "image") return (
      <span className="absolute top-2 left-2 z-10 flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
        PHOTO
      </span>
    );
    return null;
  };

  return (
    <>
      <div
        className="group relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={selectable ? onSelect : undefined}
      >
        {/* Media thumbnail */}
        <div
          className={`relative rounded-xl w-full ${thumbnailHeight} overflow-hidden ${selectable ? 'cursor-pointer' : 'cursor-pointer'}`}
          onClick={!selectable ? () => setShowPreview(true) : undefined}
        >
          {item.mediaType === "image" ? (
            <img
              src={mediaUrl}
              alt={item.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : item.mediaType === "video" ? (
            <div className="w-full h-full bg-[#0B1220] flex items-center justify-center relative">
              {/* Canvas-captured first frame — static <img>, no GPU compositing layer,
                  so it cannot bleed through the preview dialog overlay */}
              {videoThumbnail ? (
                <img
                  src={videoThumbnail}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#0D1B2A] to-[#1B263B]" />
              )}
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="rounded-full bg-black/40 p-3 flex items-center justify-center ring-2 ring-white/30 group-hover:bg-black/60 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                  </svg>
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full h-full bg-[#0D1B2A] flex items-center justify-center">
              <span className="material-icons text-slate-400 text-3xl">
                {item.mediaType === "audio" ? "mic" :
                 item.mediaType === "pdf" ? "description" : "insert_drive_file"}
              </span>
            </div>
          )}

          {/* Media type badge */}
          {mediaTypeBadge()}

          {/* Always-visible bottom gradient caption */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent rounded-xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 p-2.5 pointer-events-none">
            <p className="text-[11px] font-semibold text-white truncate leading-tight">{item.title}</p>
            <p className="text-[10px] text-white/70 mt-0.5">{formatDate(item.date || item.createdAt)}</p>
          </div>

          {/* Hover shine */}
          <div className={`absolute inset-0 rounded-xl ring-2 ring-[#E0A458]/0 transition-all duration-200 group-hover:ring-[#E0A458]/60`} />

          {/* Selection indicator */}
          {selectable && selected && (
            <div className="absolute top-2 left-2 rounded-full bg-primary text-white p-1 z-20">
              <Check className="h-4 w-4" />
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div
          className={`absolute top-2 right-2 transition-opacity z-20
            ${isHovered ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="flex gap-1">
            <Button 
              size="icon" 
              variant="secondary" 
              className="h-7 w-7 bg-white rounded-full shadow-md"
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite();
              }}
              disabled={isTogglingFavorite}
            >
              <Star 
                className={`h-3.5 w-3.5 ${item.isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-neutral-700'}`}
              />
            </Button>
            
            <Button 
              size="icon" 
              variant="secondary" 
              className="h-7 w-7 bg-white rounded-full shadow-md"
              onClick={(e) => {
                e.stopPropagation();
                setShowPreview(true);
              }}
            >
              <ExternalLink className="h-3.5 w-3.5 text-neutral-700" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button 
                  size="icon" 
                  variant="secondary" 
                  className="h-7 w-7 bg-white rounded-full shadow-md"
                >
                  <MoreVertical className="h-3.5 w-3.5 text-neutral-700" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowPreview(true)}>
                  <ExternalLink className="h-4 w-4 mr-2" /> View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDownload}>
                  <Download className="h-4 w-4 mr-2" /> Download
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    // Generate share URL
                    const shareCode = Math.random().toString(36).substring(2, 8);
                    const shareUrl = `${window.location.origin}/share/${shareCode}`;
                    
                    // Copy to clipboard
                    navigator.clipboard.writeText(shareUrl)
                      .then(() => {
                        toast({
                          title: "Link copied to clipboard",
                          description: "Share link has been copied to your clipboard. Ready to share on social media!",
                        });
                      })
                      .catch(err => {
                        console.error('Failed to copy: ', err);
                        toast({
                          title: "Failed to copy link",
                          description: "Please try again or manually share the page URL.",
                          variant: "destructive"
                        });
                      });
                  }}
                >
                  <Share className="h-4 w-4 mr-2" /> Share
                </DropdownMenuItem>
                {item.mediaType === "video" && (
                  <DropdownMenuItem onClick={() => setShowVideoEditor(true)}>
                    <Scissors className="h-4 w-4 mr-2" /> Edit Video
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem>
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="text-destructive focus:text-destructive"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  <Trash className="h-4 w-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl w-full max-h-[90vh] flex flex-col bg-[#0D1B2A] border border-[#E0A458]/30 text-white overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-white">{item.title}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {item.description || ""}
              <span className="block mt-1">
                {formatDate(item.date || item.createdAt)}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-hidden my-3">
            {renderMediaPreview()}
          </div>

          <DialogFooter className="shrink-0 flex-row items-center justify-between gap-2 sm:justify-between border-t border-[#1B263B] pt-3">
            <div className="flex gap-2">
              <Button variant="outline" className="border-[#415A77] text-slate-300 hover:bg-[#1B263B]" onClick={() => setShowPreview(false)}>
                Close
              </Button>
              <Button variant="outline" className="border-[#415A77] text-slate-300 hover:bg-[#1B263B]" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-1" /> Download
              </Button>
              <Button
                variant="outline"
                className="border-[#415A77] text-slate-300 hover:bg-[#1B263B]"
                onClick={() => {
                  const shareCode = Math.random().toString(36).substring(2, 8);
                  const shareUrl = `${window.location.origin}/share/${shareCode}`;
                  navigator.clipboard.writeText(shareUrl)
                    .then(() => toast({ title: "Link copied to clipboard", description: "Share link has been copied to your clipboard." }))
                    .catch(() => toast({ title: "Failed to copy link", variant: "destructive" }));
                }}
              >
                <Share className="h-4 w-4 mr-1" /> Share
              </Button>
            </div>
            <Button
              variant="ghost"
              className="text-red-400/70 hover:text-red-400 hover:bg-red-500/10"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <Trash className="h-4 w-4 mr-1" />
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Editor Dialog */}
      {item.mediaType === 'video' && (
        <VideoEditor
          open={showVideoEditor}
          onClose={() => setShowVideoEditor(false)}
          videoUrl={mediaUrl}
          videoId={item.id}
          videoTitle={item.title}
        />
      )}
    </>
  );
};

export default MediaItem;
